// =============================================================================
// parameters-client.ts — Client API de l'écran A-24, DOUBLABLE (Option B).
// =============================================================================
// « Option B confirmée : skeleton CI contre doubles » (GATE_HUMAN [S0]). Il n'y
// a pas encore de client HTTP typé partagé : ce module expose une INTERFACE
// (ParametersClient) miroir des endpoints D4 (apps/api/src/app-parameters) et
// une IMPLÉMENTATION stub déterministe. Le stub sera remplacé par l'impl HTTP
// réelle derrière la même interface, sans toucher l'UI.
//
// AUTORITÉ DE VALIDATION = L'API ([S2-TRANCHÉ #4]). Le stub reproduit la garde
// EF-PAR-1 (valeur hors valid_range => rejet) UNIQUEMENT pour donner un double
// fidèle aux tests : ce n'est PAS la règle métier de l'UI. L'UI consommatrice ne
// duplique aucune règle ; elle envoie la valeur et affiche l'erreur renvoyée.
//
// Les DTO ci-dessous sont des miroirs HTTP (PAS le Published Language) : sur le
// fil, les dates sont des chaînes ISO8601 (sérialisation JSON), d'où `string`.

import { HttpApiError } from '@/api/http-error';

/** Type fonctionnel d'un paramètre (miroir de ParamType côté API, DDD §3.10).
 *  `Chaine` = TEXTE libre (templates de messagerie, DBU §2.8 l.1832/l.1920-1926). */
export type ParamType = 'Pourcentage' | 'Duree' | 'Entier' | 'Booleen' | 'Chaine';

/** Borne de validation (miroir de ValidRange côté API) — bornes inclusives. */
export interface ValidRange {
  readonly min?: number;
  readonly max?: number;
}

/** Vue d'un paramètre (miroir de ParameterView D4 ; dates en ISO8601 sur le fil). */
export interface ParameterView {
  readonly paramKey: string;
  readonly paramType: ParamType;
  readonly value: unknown;
  readonly defaultValue: unknown;
  readonly validRange: ValidRange;
  readonly category: string;
  readonly description: string;
  readonly lastModifiedBy: string | null;
  /** ISO8601 ou null. */
  readonly lastModifiedAt: string | null;
}

/** Entrée d'historique (miroir de ChangeLogView D4 ; date en ISO8601 sur le fil). */
export interface ChangeLogView {
  readonly changeId: string;
  readonly paramKey: string;
  readonly oldValue: unknown;
  readonly newValue: unknown;
  readonly changedBy: string;
  /** ISO8601. */
  readonly changedAt: string;
  readonly reason: string | null;
}

/** Corps de l'édition PATCH (miroir de UpdateParameterBody D4). */
export interface UpdateParameterInput {
  readonly value: unknown;
  readonly reason?: string | undefined;
}

/**
 * Signal « campagnes en cours » (miroir de CampaignActivityDto D4, V28-IMPACT-BANNER, DBU §2.8
 * l.1854). `degraded: true` ⇒ le comptage a échoué côté API : FAIL-CLOSED INVERSÉ — l'UI AFFICHE le
 * bandeau d'impact dans ce cas, elle ne le masque JAMAIS (on avertit quand on ne sait pas).
 */
export interface CampaignActivityView {
  readonly inProgressCount: number;
  readonly degraded: boolean;
}

/**
 * Erreur applicative renvoyée par l'API (HTTP 4xx). L'UI l'affiche telle quelle :
 * elle ne réinterprète JAMAIS la règle métier, elle relaie le message serveur.
 */
export class ParametersApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'ParametersApiError');
  }
}

/** Contrat du client A-24 — miroir des endpoints AdminFull de D4. */
export interface ParametersClient {
  /** GET /app-parameters — liste de tous les paramètres. */
  list(): Promise<ParameterView[]>;
  /** GET /app-parameters/:paramKey/changelog — historique read-only. */
  changelog(paramKey: string): Promise<ChangeLogView[]>;
  /** PATCH /app-parameters/:paramKey — applique une nouvelle valeur (validée serveur). */
  update(paramKey: string, input: UpdateParameterInput): Promise<ParameterView>;
  /**
   * GET /app-parameters/campaign-activity — signal « campagnes en cours » du bandeau d'impact
   * CONDITIONNEL de l'écran A-24 (V28-IMPACT-BANNER, DBU §2.8 l.1854).
   */
  getCampaignActivity(): Promise<CampaignActivityView>;
}
